import { useRef, useState } from 'react';
import ModalPortal from '../../components/ModalPortal';
import { apiFetch } from '../../utils/api';
import { GRANTABLE_TOOL_KEYS, tools, visibleToolKeys } from '../../tools/catalog';
import { useToolAccess } from '../../tools/ToolAccessContext';

const ACCESS_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'View', label: 'View' },
    { value: 'Edit', label: 'Edit' }
];

export default function ApproveAccessModal({ request, onClose, onApproved }) {
    const { isSuperAdmin } = useToolAccess();
    const grantableToolKeys = visibleToolKeys(GRANTABLE_TOOL_KEYS, isSuperAdmin);
    const overlayMouseDown = useRef(false);
    const [primaryToolKey, setPrimaryToolKey] = useState('Logic');
    const [extras, setExtras] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!request) return null;

    const handleClose = () => {
        if (saving) return;
        setPrimaryToolKey('Logic');
        setExtras({});
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        const extra = Object.entries(extras)
            .filter(([, access]) => access)
            .filter(([key]) => key !== primaryToolKey)
            .map(([key, access]) => ({ key, access }));
        try {
            const res = await apiFetch(`/api/auth/organization/access-requests/${request.id}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ primaryToolKey, tools: extra })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || 'Unable to approve the request.');
            setPrimaryToolKey('Logic');
            setExtras({});
            await onApproved?.();
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    return (
        <ModalPortal>
            <div
                className="modal-overlay"
                onMouseDown={e => { if (e.target === e.currentTarget) overlayMouseDown.current = true; }}
                onMouseUp={e => {
                    if (e.target === e.currentTarget && overlayMouseDown.current) handleClose();
                    overlayMouseDown.current = false;
                }}
            >
                <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '440px', maxWidth: '560px' }}>
                    <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Approve access</h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
                        {request.fullName || request.email} will join with Product edit unless you choose another primary tool.
                    </p>
                    {error && <div style={{ marginBottom: '1rem', color: 'var(--error)', fontSize: '0.9rem' }}>{error}</div>}
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Primary tool</label>
                            <select className="input-field" value={primaryToolKey} onChange={e => setPrimaryToolKey(e.target.value)} style={{ margin: 0, width: '100%' }}>
                                {grantableToolKeys.map(key => (
                                    <option key={key} value={key}>{tools[key].label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            {grantableToolKeys.filter(key => key !== primaryToolKey).map(key => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.9rem' }}>
                                    <span>{tools[key].label}</span>
                                    <select
                                        className="input-field"
                                        value={extras[key] || ''}
                                        onChange={e => setExtras(prev => ({ ...prev, [key]: e.target.value }))}
                                        style={{ margin: 0, width: '120px' }}
                                    >
                                        {ACCESS_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Approving…' : 'Approve'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </ModalPortal>
    );
}
